import React from "react";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  AlertTriangle,
  CheckCircle,
  Database,
  Users,
  Calendar,
  Trophy,
} from "lucide-react";
import {
  listPlayerConstructs,
  listEventPlayerResults,
  listEvents,
  listLeagues,
  listUsers,
  listEventStandings,
} from "../../graphql/queries";

const client = generateClient();

const DataAudit: React.FC = () => {
  // Fetch all data types
  const {
    data: playerConstructs,
    isLoading: constructsLoading,
    error: constructsError,
  } = useQuery({
    queryKey: ["auditPlayerConstructs"],
    queryFn: async () => {
      try {
        const result = await client.graphql({
          query: listPlayerConstructs,
          variables: { limit: 1000 },
        });
        return result.data.listPlayerConstructs.items;
      } catch (error) {
        console.error("Error fetching PlayerConstructs:", error);
        throw error;
      }
    },
  });

  const {
    data: eventPlayerResults,
    isLoading: resultsLoading,
    error: resultsError,
  } = useQuery({
    queryKey: ["auditEventPlayerResults"],
    queryFn: async () => {
      try {
        const result = await client.graphql({
          query: listEventPlayerResults,
          variables: { limit: 1000 },
        });
        return result.data.listEventPlayerResults.items;
      } catch (error) {
        console.error("Error fetching EventPlayerResults:", error);
        throw error;
      }
    },
  });

  const {
    data: events,
    isLoading: eventsLoading,
    error: eventsError,
  } = useQuery({
    queryKey: ["auditEvents"],
    queryFn: async () => {
      try {
        const result = await client.graphql({
          query: listEvents,
          variables: { limit: 1000 },
        });
        return result.data.listEvents.items;
      } catch (error) {
        console.error("Error fetching Events:", error);
        throw error;
      }
    },
  });

  const {
    data: leagues,
    isLoading: leaguesLoading,
    error: leaguesError,
  } = useQuery({
    queryKey: ["auditLeagues"],
    queryFn: async () => {
      try {
        const result = await client.graphql({
          query: listLeagues,
          variables: { limit: 1000 },
        });
        return result.data.listLeagues.items;
      } catch (error) {
        console.error("Error fetching Leagues:", error);
        throw error;
      }
    },
  });

  const {
    data: users,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["auditUsers"],
    queryFn: async () => {
      try {
        const result = await client.graphql({
          query: listUsers,
          variables: { limit: 1000 },
        });
        return result.data.listUsers.items;
      } catch (error) {
        console.error("Error fetching Users:", error);
        throw error;
      }
    },
  });

  const {
    data: eventStandings,
    isLoading: standingsLoading,
    error: standingsError,
  } = useQuery({
    queryKey: ["auditEventStandings"],
    queryFn: async () => {
      try {
        const result = await client.graphql({
          query: listEventStandings,
          variables: { limit: 1000 },
        });
        return result.data.listEventStandings.items;
      } catch (error) {
        console.error("Error fetching EventStandings:", error);
        throw error;
      }
    },
  });

  const isLoading =
    constructsLoading ||
    resultsLoading ||
    eventsLoading ||
    leaguesLoading ||
    usersLoading ||
    standingsLoading;

  // Calculate data health
  const dataHealth = {
    playerConstructs: {
      count: playerConstructs?.length || 0,
      hasData: (playerConstructs?.length || 0) > 0,
      error: constructsError,
    },
    eventPlayerResults: {
      count: eventPlayerResults?.length || 0,
      hasData: (eventPlayerResults?.length || 0) > 0,
      error: resultsError,
    },
    events: {
      count: events?.length || 0,
      hasData: (events?.length || 0) > 0,
      error: eventsError,
    },
    leagues: {
      count: leagues?.length || 0,
      hasData: (leagues?.length || 0) > 0,
      error: leaguesError,
    },
    users: {
      count: users?.length || 0,
      hasData: (users?.length || 0) > 0,
      error: usersError,
    },
    eventStandings: {
      count: eventStandings?.length || 0,
      hasData: (eventStandings?.length || 0) > 0,
      error: standingsError,
    },
  };

  const hasAnyData = Object.values(dataHealth).some((item) => item.hasData);
  const hasCriticalData =
    dataHealth.playerConstructs.hasData ||
    dataHealth.eventPlayerResults.hasData;

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">Auditing database...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <Database className="h-8 w-8 text-primary" />
        <h1 className="text-3xl font-bold">Database Audit Report</h1>
      </div>

      {/* Overall Status */}
      <Card
        className={`mb-6 ${hasAnyData ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {hasAnyData ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-600" />
            )}
            Database Status
          </CardTitle>
          <CardDescription>
            {hasAnyData
              ? "Data found in database"
              : "No data found in database"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hasCriticalData ? (
            <div className="text-green-800">
              <p className="font-medium">✅ Critical data is present</p>
              <p className="text-sm">
                PlayerConstructs and/or EventPlayerResults exist
              </p>
            </div>
          ) : (
            <div className="text-red-800">
              <p className="font-medium">⚠️ Critical data is missing</p>
              <p className="text-sm">
                No PlayerConstructs or EventPlayerResults found
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Tables */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              PlayerConstructs
            </CardTitle>
            <CardDescription>EventLink imported player data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div
                className={`text-3xl font-bold ${dataHealth.playerConstructs.hasData ? "text-green-600" : "text-red-600"}`}
              >
                {dataHealth.playerConstructs.count}
              </div>
              <div className="text-sm text-muted-foreground">records</div>
              {dataHealth.playerConstructs.error && (
                <div className="text-xs text-red-600 mt-2">
                  Error: {dataHealth.playerConstructs.error.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              EventPlayerResults
            </CardTitle>
            <CardDescription>Individual event results</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div
                className={`text-3xl font-bold ${dataHealth.eventPlayerResults.hasData ? "text-green-600" : "text-red-600"}`}
              >
                {dataHealth.eventPlayerResults.count}
              </div>
              <div className="text-sm text-muted-foreground">records</div>
              {dataHealth.eventPlayerResults.error && (
                <div className="text-xs text-red-600 mt-2">
                  Error: {dataHealth.eventPlayerResults.error.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Events
            </CardTitle>
            <CardDescription>Tournament events</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div
                className={`text-3xl font-bold ${dataHealth.events.hasData ? "text-green-600" : "text-red-600"}`}
              >
                {dataHealth.events.count}
              </div>
              <div className="text-sm text-muted-foreground">records</div>
              {dataHealth.events.error && (
                <div className="text-xs text-red-600 mt-2">
                  Error: {dataHealth.events.error.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Users
            </CardTitle>
            <CardDescription>User accounts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div
                className={`text-3xl font-bold ${dataHealth.users.hasData ? "text-green-600" : "text-red-600"}`}
              >
                {dataHealth.users.count}
              </div>
              <div className="text-sm text-muted-foreground">records</div>
              {dataHealth.users.error && (
                <div className="text-xs text-red-600 mt-2">
                  Error: {dataHealth.users.error.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              EventStandings
            </CardTitle>
            <CardDescription>Legacy event standings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div
                className={`text-3xl font-bold ${dataHealth.eventStandings.hasData ? "text-green-600" : "text-red-600"}`}
              >
                {dataHealth.eventStandings.count}
              </div>
              <div className="text-sm text-muted-foreground">records</div>
              {dataHealth.eventStandings.error && (
                <div className="text-xs text-red-600 mt-2">
                  Error: {dataHealth.eventStandings.error.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Leagues
            </CardTitle>
            <CardDescription>League configurations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div
                className={`text-3xl font-bold ${dataHealth.leagues.hasData ? "text-green-600" : "text-red-600"}`}
              >
                {dataHealth.leagues.count}
              </div>
              <div className="text-sm text-muted-foreground">records</div>
              {dataHealth.leagues.error && (
                <div className="text-xs text-red-600 mt-2">
                  Error: {dataHealth.leagues.error.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sample Data */}
      {hasAnyData && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Sample Data</CardTitle>
            <CardDescription>First few records from each table</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dataHealth.playerConstructs.hasData && (
                <div>
                  <h4 className="font-medium mb-2">PlayerConstructs:</h4>
                  <div className="bg-gray-100 p-3 rounded text-sm font-mono">
                    {JSON.stringify(playerConstructs?.slice(0, 2), null, 2)}
                  </div>
                </div>
              )}
              {dataHealth.eventPlayerResults.hasData && (
                <div>
                  <h4 className="font-medium mb-2">EventPlayerResults:</h4>
                  <div className="bg-gray-100 p-3 rounded text-sm font-mono">
                    {JSON.stringify(eventPlayerResults?.slice(0, 2), null, 2)}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          {!hasCriticalData ? (
            <div className="text-red-800">
              <p className="font-medium mb-2">
                ⚠️ Critical Issue: No EventLink data found
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Check if EventLink imports are working properly</li>
                <li>
                  Verify that events are being created when importing data
                </li>
                <li>Check the EventResults import page for any errors</li>
                <li>Consider re-importing recent EventLink data</li>
              </ul>
            </div>
          ) : (
            <div className="text-green-800">
              <p className="font-medium mb-2">✅ Data appears to be intact</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>PlayerConstructs and EventPlayerResults exist</li>
                <li>Public standings should be working</li>
                <li>Check the leaderboard page for current standings</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DataAudit;

